<template>
  <div>
    <Card cardClasses="border-0 shadow-sm mb-5">
      <template slot="header">
        <div class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0 text-primary">
            <vue-feather type="user" size="20" class="me-2"></vue-feather>
            Employee Details
          </h4>
          <div class="d-flex gap-2">
            <button class="btn btn-light btn-sm d-flex align-items-center shadow-sm" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
            <button v-if="profile.role !== 'user' && profile.role !== 'admin'" class="btn btn-primary btn-sm d-flex align-items-center shadow-sm" @click="editEmployee">
              <vue-feather type="edit" size="16" class="me-1"></vue-feather>
              Edit Employee
            </button>
            <button v-if="profile.role !== 'user'" class="btn btn-info btn-sm d-flex align-items-center text-white shadow-sm" @click="changeStatusModal = true">
              <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
              Change Status
            </button>
            <button v-if="profile.role !== 'user'" class="btn btn-success btn-sm d-flex align-items-center shadow-sm" @click="p9Modal = true">
              <vue-feather type="download" size="16" class="me-1"></vue-feather>
              Download P9
            </button>
          </div>
        </div>
      </template>
      <div class="row">
        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
        </div>
        <div v-if="!loading" class="employee-profile mb-4">
          <div class="row align-items-center">
            <div class="col-md-3 col-lg-2 text-center mb-3 mb-md-0">
              <div class="position-relative d-inline-block">
                <img class="rounded-circle img-thumbnail shadow-sm" style="width: 150px; height: 150px; object-fit: cover;" :src="avatar" alt="">
                <button class="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle p-2" @click="$refs.refInputEl.click()" title="Upload Photo">
                  <vue-feather type="camera" size="16"></vue-feather>
                </button>
                <input
                    ref="refInputEl"
                    type="file"
                    name="logo"
                    accept=".jpeg,.png,.jpg"
                    hidden
                    @input="changeAvatar"
                >
              </div>
            </div>
            <div class="col-md-9 col-lg-10">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                <h2 class="mb-0">{{ name }}</h2>
                <div>
                  <badge class="ms-2 badge-lg" :type="getPillByStatus(employee.status)">
                    <vue-feather :type="getIconByStatus(employee.status)" size="14" class="me-1"></vue-feather>
                    {{ employee.status }}
                  </badge>
                </div>
              </div>
              <div class="row g-3">
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="briefcase" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Designation</div>
                      <div class="fw-medium">{{ employee.designation.name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="layers" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Department</div>
                      <div class="fw-medium">{{ employee.department.name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="map-pin" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Work Station</div>
                      <div class="fw-medium">{{ employee.station.name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="hash" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Employee No</div>
                      <div class="fw-medium">{{ employee.employee_no }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="border-1 border-light border-opacity-75 my-3" />

        <!-- Tabs Navigation -->
        <div class="row mb-4">
          <div class="col-12">
            <ul class="nav nav-tabs nav-fill nav-justified w-100">
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'details' }"
                  @click="activeTab = 'details'"
                >
                  Details
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'allowances' }"
                  @click="activeTab = 'allowances'"
                >
                  Allowances
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'incomes' }"
                  @click="activeTab = 'incomes'"
                >
                  Recurrent Incomes
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'deductions' }"
                  @click="activeTab = 'deductions'"
                >
                  Recurrent Deductions
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'revenues' }"
                  @click="activeTab = 'revenues'"
                >
                  Commission / Revenues
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link w-100 py-3 fw-semibold"
                  :class="{ active: activeTab === 'files' }"
                  @click="activeTab = 'files'"
                >
                  Files
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="row">
          <div class="col-12">
            <!-- Details Tab -->
            <div v-if="activeTab === 'details'">
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-if="!loading" class="pt-3">
              <div class="row">
                <div class="col-12 mb-4">
                  <div class="card border-0 bg-light rounded-3">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="user" size="18" class="text-primary me-2"></vue-feather>
                        Personal Information
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">ID Number</div>
                            <div class="fw-medium">{{ employee.id_number }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Gender</div>
                            <div class="fw-medium">{{ employee.gender }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Date of Birth</div>
                            <div class="fw-medium">{{ formatDate(employee.dob) }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Phone</div>
                            <div class="fw-medium">{{ employee.phone }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Email</div>
                            <div class="fw-medium">{{ employee.email }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Address</div>
                            <div class="fw-medium">{{ employee.address }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 mb-4">
                  <div class="card border-0 bg-light rounded-3 h-100">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="briefcase" size="18" class="text-primary me-2"></vue-feather>
                        Employment Details
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Employment Date</div>
                            <div class="fw-medium">{{ formatDate(employee.employment_date) }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Terms of Service</div>
                            <div class="fw-medium">{{ employee.tos }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Salary</div>
                            <div class="fw-medium">{{ Ksh(employee.salary) }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Termination Date</div>
                            <div class="fw-medium">{{ !employee.termination_date ? 'N/A' : formatDate(employee.termination_date) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 mb-4">
                  <div class="card border-0 bg-light rounded-3 h-100">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="users" size="18" class="text-primary me-2"></vue-feather>
                        Next of Kin
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Name</div>
                            <div class="fw-medium">{{ employee.kin.name }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Relationship</div>
                            <div class="fw-medium">{{ employee.kin.relationship }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="detail-item">
                            <div class="text-muted small">Phone</div>
                            <div class="fw-medium">{{ employee.kin.phone }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="card border-0 bg-light rounded-3">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="credit-card" size="18" class="text-primary me-2"></vue-feather>
                        Financial Information
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">PIN No</div>
                            <div class="fw-medium">{{ employee.details.pin_no }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">SHIF No</div>
                            <div class="fw-medium">{{ employee.details.nhif_no }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">NSSF No</div>
                            <div class="fw-medium">{{ employee.details.nssf_no }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">Bank Name</div>
                            <div class="fw-medium">{{ employee.details.bank_name }}</div>
                            <div class="text-muted small">Code: {{ employee.details.bank_code }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">Account No</div>
                            <div class="fw-medium">{{ employee.details.account_no }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-2">
                          <div class="detail-item">
                            <div class="text-muted small">Branch Name</div>
                            <div class="fw-medium">{{ employee.details.branch_name }}</div>
                            <div class="text-muted small">Code: {{ employee.details.branch_code }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Allowances Tab -->
          <div v-if="activeTab === 'allowances'">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="dollar-sign" size="18" class="text-primary me-2"></vue-feather>
                  Employee Allowances
                </h5>
                <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-primary btn-sm" @click="allowanceModal = true; allowanceMode = 'add'">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Allowance
                </button>
              </div>

              <div v-if="allowances.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="dollar-sign" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No allowances found</h5>
                <p class="text-muted small mb-0">Add an allowance to see it here</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Amount</th>
                      <th scope="col" class="border-0">Taxable</th>
                      <th scope="col" class="border-0">Created</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="allowance in allowances" :key="allowance.id">
                      <td class="align-middle">{{ allowance.name }}</td>
                      <td class="align-middle">{{ Ksh(allowance.amount) }}</td>
                      <td class="align-middle">
                        <badge :type="allowance.taxable ? 'success' : 'secondary'" class="badge-sm">
                          {{ allowance.taxable ? 'Yes' : 'No' }}
                        </badge>
                      </td>
                      <td class="align-middle">{{ formatDate(allowance.created_at) }}</td>
                      <td class="align-middle text-end">
                        <button
                          v-if="profile.role !== 'user' && !isEmployee"
                          class="btn btn-outline-primary btn-sm me-1"
                          @click="allowanceModal = true; allowanceMode = 'edit'; populateAllowanceEdit(allowance)"
                        >
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button
                          v-if="profile.role !== 'user' && !isEmployee"
                          class="btn btn-outline-danger btn-sm"
                          @click="confirmDialog('Allowance', () => deleteAllowance(allowance.id))"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Recurrent Incomes Tab -->
          <div v-if="activeTab === 'incomes'">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="trending-up" size="18" class="text-primary me-2"></vue-feather>
                  Recurrent Incomes
                </h5>
                <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-primary btn-sm" @click="incomeModal = true; incomeMode = 'add'">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Recurrent Income
                </button>
              </div>

              <div v-if="incomes.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="trending-up" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No recurrent incomes found</h5>
                <p class="text-muted small mb-0">Add a recurrent income to see it here</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Amount</th>
                      <th scope="col" class="border-0">Taxable</th>
                      <th scope="col" class="border-0">From</th>
                      <th scope="col" class="border-0">To</th>
                      <th scope="col" class="border-0">Description</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="income in incomes" :key="income.id">
                      <td class="align-middle">{{ income.name }}</td>
                      <td class="align-middle">{{ Ksh(income.amount) }}</td>
                      <td class="align-middle">
                        <badge :type="income.taxable ? 'success' : 'secondary'" class="badge-sm">
                          {{ income.taxable ? 'Yes' : 'No' }}
                        </badge>
                      </td>
                      <td class="align-middle">{{ formatDate(income.from) }}</td>
                      <td class="align-middle">{{ formatDate(income.to) }}</td>
                      <td class="align-middle">
                        <span v-if="income.description" class="text-truncate d-inline-block" style="max-width: 150px;" :title="income.description">
                          {{ income.description }}
                        </span>
                        <span v-else class="text-muted fst-italic">—</span>
                      </td>
                      <td class="align-middle text-end">
                        <button
                          v-if="profile.role !== 'user' && !isEmployee"
                          class="btn btn-outline-primary btn-sm me-1"
                          @click="incomeModal = true; incomeMode = 'edit'; populateIncomeEdit(income)"
                        >
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button
                          v-if="profile.role !== 'user' && !isEmployee"
                          class="btn btn-outline-danger btn-sm"
                          @click="confirmDialog('Recurrent Income', () => deleteIncome(income.id))"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Recurrent Deductions Tab -->
          <div v-if="activeTab === 'deductions'">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="trending-down" size="18" class="text-primary me-2"></vue-feather>
                  Recurrent Deductions
                </h5>
                <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-primary btn-sm" @click="deductionModal = true; deductionMode = 'add'">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Recurrent Deduction
                </button>
              </div>

              <div v-if="deductions.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="trending-down" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No recurrent deductions found</h5>
                <p class="text-muted small mb-0">Add a recurrent deduction to see it here</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Amount</th>
                      <th scope="col" class="border-0">Taxable</th>
                      <th scope="col" class="border-0">From</th>
                      <th scope="col" class="border-0">To</th>
                      <th scope="col" class="border-0">Description</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="deduction in deductions" :key="deduction.id">
                      <td class="align-middle">{{ deduction.name }}</td>
                      <td class="align-middle">{{ Ksh(deduction.amount) }}</td>
                      <td class="align-middle">
                        <badge :type="deduction.taxable ? 'success' : 'secondary'" class="badge-sm">
                          {{ deduction.taxable ? 'Yes' : 'No' }}
                        </badge>
                      </td>
                      <td class="align-middle">{{ formatDate(deduction.from) }}</td>
                      <td class="align-middle">{{ formatDate(deduction.to) }}</td>
                      <td class="align-middle">
                        <span v-if="deduction.description" class="text-truncate d-inline-block" style="max-width: 150px;" :title="deduction.description">
                          {{ deduction.description }}
                        </span>
                        <span v-else class="text-muted fst-italic">—</span>
                      </td>
                      <td class="align-middle text-end">
                        <button
                            v-if="profile.role !== 'user' && !isEmployee"
                            class="btn btn-outline-primary btn-sm me-1"
                            @click="deductionModal = true; deductionMode = 'edit'; populateDeductionEdit(deduction)"
                        >
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button
                            v-if="profile.role !== 'user' && !isEmployee"
                            class="btn btn-outline-danger btn-sm"
                            @click="confirmDialog('Recurrent Deduction', () => deleteDeduction(deduction.id))"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Commission / Revenues Tab -->
          <div v-if="activeTab === 'revenues'">
            <div class="pt-3">
              <div class="row">
                <div class="col-md-12">
                  <div class="card border-0 h-100">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="mb-0 d-flex align-items-center">
                          <vue-feather type="bar-chart-2" size="18" class="text-primary me-2"></vue-feather>
                          Revenues
                        </h5>
                        <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-primary btn-sm" @click="employeeRevenueModal = true; employeeRevenueMode = 'add'">
                          <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                          Add Revenue
                        </button>
                      </div>

                      <div v-if="employeeRevenues.length === 0" class="text-center py-5 bg-light rounded-3">
                        <vue-feather type="bar-chart-2" size="48" class="text-muted mb-3"></vue-feather>
                        <h5 class="text-muted">No revenues found</h5>
                        <p class="text-muted small mb-0">Add a revenue to see it here</p>
                      </div>

                      <div v-else class="table-responsive">
                        <table class="table table-hover border-0">
                          <thead class="table-light">
                            <tr>
                              <th scope="col" class="border-0">Name</th>
                              <th scope="col" class="border-0">Month</th>
                              <th scope="col" class="border-0">Year</th>
                              <th scope="col" class="border-0">Target</th>
                              <th scope="col" class="border-0">Revenue</th>
                              <th scope="col" class="border-0">Comm. Amount</th>
                              <th scope="col" class="border-0">Rate(%)</th>
                              <th scope="col" class="border-0">Commission</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="employeeRevenue in employeeRevenues" :key="employeeRevenue.id">
                              <td class="align-middle">{{ employeeRevenue.name }}</td>
                              <td class="align-middle">{{ employeeRevenue.month }}</td>
                              <td class="align-middle">{{ employeeRevenue.year }}</td>
                              <td class="align-middle">{{ Ksh(employeeRevenue.target) }}</td>
                              <td class="align-middle">{{ Ksh(employeeRevenue.amount) }}</td>
                              <td class="align-middle">{{ Ksh(employeeRevenue.amount - employeeRevenue.target) }}</td>
                              <td class="align-middle">{{ employeeRevenue.rate }}%</td>
                              <td class="align-middle">{{ Ksh((employeeRevenue.amount - employeeRevenue.target) * (employeeRevenue.rate / 100)) }}</td>
                              <td class="align-middle text-end">
                                <button
                                  v-if="profile.role !== 'user' && !isEmployee && employeeRevenue.status === 'draft'"
                                  class="btn btn-outline-primary btn-sm"
                                  @click="employeeRevenueModal = true; employeeRevenueMode = 'edit'; populateEmployeeRevenueEdit(employeeRevenue)"
                                >
                                  <vue-feather type="edit" size="14"></vue-feather>
                                </button>
                              </td>
                            </tr>
                          </tbody>
                          <tfoot class="table-light">
                            <tr>
                              <td colspan="4" class="text-end fw-bold">Totals:</td>
                              <td class="fw-bold">{{ Ksh(totalRevenue) }}</td>
                              <td></td>
                              <td></td>
                              <td class="fw-bold">{{ Ksh(totalCommission) }}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Files Tab -->
          <div v-if="activeTab === 'files'">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="file" size="18" class="text-primary me-2"></vue-feather>
                  Employee Files
                </h5>
                <div v-if="profile.role !== 'user' && !isEmployee" class="d-flex gap-2">
                  <button class="btn btn-outline-info btn-sm" @click="fileRequestModal = true">
                    <vue-feather type="file-text" size="16" class="me-1"></vue-feather>
                    Request File
                  </button>
                  <button class="btn btn-primary btn-sm" @click="openFileModal">
                    <vue-feather type="upload" size="16" class="me-1"></vue-feather>
                    Add File
                  </button>
                </div>
              </div>

              <div v-if="employeeFiles.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="file" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No files found</h5>
                <p class="text-muted small mb-0">Upload files to see them here</p>
              </div>

              <div v-else class="table-responsive mb-5">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Description</th>
                      <th scope="col" class="border-0">Created</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="file in employeeFiles" :key="file.id">
                      <td class="align-middle">
                        <div class="d-flex align-items-center">
                          <vue-feather
                            :type="getFileIcon(file.file)"
                            size="16"
                            class="text-primary me-2"
                          ></vue-feather>
                          {{ file.name }}
                        </div>
                      </td>
                      <td class="align-middle">
                        <span v-if="file.description" class="text-truncate d-inline-block" style="max-width: 250px;" :title="file.description">
                          {{ file.description }}
                        </span>
                        <span v-else class="text-muted fst-italic">—</span>
                      </td>
                      <td class="align-middle">{{ formatDate(file.created_at) }}</td>
                      <td class="align-middle text-end">
                        <button
                          class="btn btn-outline-primary btn-sm"
                          @click="viewFile(file.file)"
                          title="View File"
                        >
                          <vue-feather type="eye" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- File Requests Section -->
              <div class="mt-5">
                <h5 class="mb-3 d-flex align-items-center">
                  <vue-feather type="file-text" size="18" class="text-primary me-2"></vue-feather>
                  File Requests
                </h5>

                <div v-if="employeeFileRequests.length === 0" class="text-center py-4 bg-light rounded-3">
                  <vue-feather type="file-text" size="32" class="text-muted mb-2"></vue-feather>
                  <p class="text-muted mb-0">No file requests found</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-hover border-0">
                    <thead class="table-light">
                      <tr>
                        <th scope="col" class="border-0">File Name</th>
                        <th scope="col" class="border-0">Description</th>
                        <th scope="col" class="border-0">Requested By</th>
                        <th scope="col" class="border-0">Status</th>
                        <th scope="col" class="border-0">Requested</th>
                        <th scope="col" class="border-0">Submitted</th>
                        <th scope="col" class="border-0 text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="request in employeeFileRequests" :key="request.id">
                        <td class="align-middle">
                          <div class="d-flex align-items-center">
                            <vue-feather type="file-text" size="16" class="text-primary me-2"></vue-feather>
                            <span class="fw-medium">{{ request.file_request.name }}</span>
                          </div>
                        </td>
                        <td class="align-middle">
                          <span v-if="request.file_request.description" class="text-truncate d-inline-block" style="max-width: 200px;" :title="request.file_request.description">
                            {{ request.file_request.description }}
                          </span>
                          <span v-else class="text-muted fst-italic">—</span>
                        </td>
                        <td class="align-middle">
                          <span v-if="request.file_request.user">
                            {{ request.file_request.user.first_name }} {{ request.file_request.user.last_name }}
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="align-middle">
                          <span class="badge" :class="getFileRequestStatusClass(request.status)">
                            {{ request.status }}
                          </span>
                        </td>
                        <td class="align-middle">{{ formatDate(request.created_at) }}</td>
                        <td class="align-middle">
                          <span v-if="request.submitted_at">{{ formatDate(request.submitted_at) }}</span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="align-middle text-end">
                          <button
                            v-if="request.status === 'pending' && isEmployee"
                            class="btn btn-sm btn-primary"
                            @click="openFileModalForRequest(request)"
                            title="Upload File"
                          >
                            <vue-feather type="upload" size="14" class="me-1"></vue-feather>
                            Upload
                          </button>
                          <span v-else class="text-muted small">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        <hr class="border-1 border-info border-opacity-75 mt-3" />
        <div class="col-md-12 d-flex justify-content-center gap-4">
          <button class="btn btn-light d-flex align-items-center shadow-sm px-3 py-2" @click="$router.go(-1)">
            <vue-feather type="arrow-left" size="16" class="me-2"></vue-feather>
            Back
          </button>
          <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-primary d-flex align-items-center shadow-sm px-3 py-2" @click="editEmployee">
            <vue-feather type="edit" size="16" class="me-2"></vue-feather>
            Edit Employee
          </button>
          <button v-if="profile.role !== 'user' && !isEmployee" class="btn btn-info d-flex align-items-center text-white shadow-sm px-3 py-2" @click="changeStatusModal = true">
            <vue-feather type="refresh-cw" size="16" class="me-2"></vue-feather>
            Change Status
          </button>
          <button v-if="profile.role !== 'user'" class="btn btn-success d-flex align-items-center shadow-sm px-3 py-2" @click="p9Modal = true">
            <vue-feather type="download" size="16" class="me-2"></vue-feather>
            Download P9
          </button>
        </div>
      </div>

      <!-- Add File Modal -->
      <Modal :open="fileModal" @closed="closeFileModal">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="upload" size="18" class="text-primary me-2"></vue-feather>
            Upload File
          </h5>
          <button @click="closeFileModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="row g-3">
              <div class="col-12">
                <label for="uploadName" class="form-label">File Name</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="file" size="16"></vue-feather>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    list="uploadOptions"
                    id="uploadName"
                    v-model="file.name"
                    placeholder="Enter file name or select from suggestions"
                  >
                </div>
                <datalist id="uploadOptions">
                  <option v-for="upload in suggestions.files" :key="upload" :value="upload"></option>
                </datalist>
              </div>

              <div class="col-12">
                <label class="form-label">File Upload</label>
                <div class="input-group">
                  <input
                    class="form-control"
                    type="file"
                    @input="handleFileUpload"
                    name="docs"
                    accept=".jpeg,.png,.jpg,.pdf"
                  >
                </div>
                <small class="text-muted">Supported formats: JPEG, PNG, JPG, PDF</small>
              </div>

              <div class="col-12">
                <label for="fileDescription" class="form-label">Description</label>
                <textarea
                  id="fileDescription"
                  class="form-control"
                  v-model="file.description"
                  rows="3"
                  placeholder="Enter a description for this file (optional)"
                ></textarea>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="closeFileModal">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="addFile">
            <vue-feather type="upload" size="16" class="me-1"></vue-feather>
            Upload
          </button>
        </div>
      </Modal>
      <!-- Add File Modal End -->

      <!-- Download P9 Modal -->
      <Modal :open="p9Modal" @closed="p9Modal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="file-text" size="18" class="text-primary me-2"></vue-feather>
            Download P9 Form
          </h5>
          <button @click="p9Modal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="mb-3">
              <label for="p9Year" class="form-label">Select Tax Year</label>
              <div class="input-group">
                <span class="input-group-text bg-light">
                  <vue-feather type="calendar" size="16"></vue-feather>
                </span>
                <select
                  id="p9Year"
                  class="form-select"
                  v-model="year"
                  data="">
                  <option value="" disabled selected>Select year</option>
                  <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <small class="text-muted mt-2 d-block">
                The P9 form contains the employee's tax information for the selected year.
              </small>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="p9Modal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="year === ''"
            @click="p9Download"
          >
            <vue-feather type="download" size="16" class="me-1"></vue-feather>
            Download
          </button>
        </div>
      </Modal>
      <!-- Download P9 Modal End -->

      <!-- Request File Modal -->
      <Modal :open="fileRequestModal" @closed="fileRequestModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 fw-bold">
            <vue-feather type="file-text" size="20" class="me-2"></vue-feather>
            Request File from Employee
          </h5>
          <button @click="fileRequestModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitEmployeeFileRequest">
            <div class="mb-3">
              <label for="requestFileName" class="form-label fw-semibold">File Name <span class="small fw-lighter">(Request single file)</span>  <span class="text-danger">*</span></label>
              <input
                type="text"
                id="requestFileName"
                class="form-control"
                v-model="fileRequestData.name"
                placeholder="Enter file name"
                required
              >
            </div>
            <div class="mb-3">
              <label for="requestFileDescription" class="form-label fw-semibold">Description <span class="text-muted">(optional)</span></label>
              <textarea
                id="requestFileDescription"
                class="form-control"
                v-model="fileRequestData.description"
                placeholder="Enter file description"
                rows="3"
              ></textarea>
            </div>
            <div class="alert alert-info d-flex align-items-center" role="alert">
              <vue-feather type="info" size="16" class="me-2"></vue-feather>
              <small>This file request will be sent to {{ name }}</small>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0 pt-0">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="fileRequestModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="submitEmployeeFileRequest"
            :disabled="!fileRequestData.name || savingFileRequest"
          >
            <span v-if="savingFileRequest" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Send Request
          </button>
        </div>
      </Modal>

      <!-- Change Employee Status Modal -->
      <Modal :open="changeStatusModal" @closed="changeStatusModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="refresh-cw" size="18" class="text-primary me-2"></vue-feather>
            Change Employee Status
          </h5>
          <button @click="changeStatusModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="mb-3">
              <label for="employeeStatus" class="form-label">Select Status</label>
              <div class="input-group">
                <span class="input-group-text bg-light">
                  <vue-feather :type="getIconByStatus(employeeStatus)" size="16"></vue-feather>
                </span>
                <select
                  id="employeeStatus"
                  class="form-select"
                  v-model="employeeStatus"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              <div class="mt-3">
                <div class="status-description p-3 rounded-3" :class="getStatusDescriptionClass(employeeStatus)">
                  <div class="d-flex align-items-start">
                    <vue-feather :type="getIconByStatus(employeeStatus)" size="20" class="me-2 mt-1"></vue-feather>
                    <div>
                      <h6 class="mb-1">{{ getStatusTitle(employeeStatus) }}</h6>
                      <p class="mb-0 small">{{ getStatusDescription(employeeStatus) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="changeStatusModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="changeStatus"
          >
            <vue-feather type="save" size="16" class="me-1"></vue-feather>
            Update Status
          </button>
        </div>
      </Modal>
      <!-- Change Employee Status Modal End -->

      <!-- Add Allowance Modal -->
      <Modal :open="allowanceModal" @closed="allowanceModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="dollar-sign" size="18" class="text-primary me-2"></vue-feather>
            {{ allowanceMode === 'add' ? 'Add Allowance' : 'Edit Allowance' }}
          </h5>
          <button @click="allowanceModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="row g-3">
              <div class="col-12">
                <label for="allowanceName" class="form-label">Allowance Name</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="tag" size="16"></vue-feather>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    list="allowanceOptions"
                    id="allowanceName"
                    v-model="allowance.name"
                    placeholder="Enter allowance name or select from suggestions"
                  >
                </div>
                <datalist id="allowanceOptions">
                  <option v-for="allowance in suggestions.allowances" :key="allowance" :value="allowance"></option>
                </datalist>
              </div>

              <div class="col-12">
                <label for="allowanceAmount" class="form-label">Amount</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="dollar-sign" size="16"></vue-feather>
                  </span>
                  <input
                    type="number"
                    class="form-control"
                    id="allowanceAmount"
                    v-model="allowance.amount"
                    placeholder="Enter amount"
                  >
                </div>
              </div>

              <div class="col-12">
                <label class="form-label d-block">Taxable</label>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="taxableOptions"
                    id="taxableYes"
                    :value="1"
                    v-model="allowance.taxable"
                  >
                  <label class="form-check-label" for="taxableYes">
                    <badge type="success" class="badge-sm">Yes</badge>
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="taxableOptions"
                    id="taxableNo"
                    :value="0"
                    v-model="allowance.taxable"
                  >
                  <label class="form-check-label" for="taxableNo">
                    <badge type="secondary" class="badge-sm">No</badge>
                  </label>
                </div>
                <small class="text-muted d-block mt-1">
                  Taxable allowances will be included in the employee's taxable income.
                </small>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="allowanceModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="allowanceMode === 'add'"
            @click="addAllowance"
          >
            <vue-feather type="plus" size="16" class="me-1"></vue-feather>
            Add Allowance
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateAllowance($event, allowance.id)"
          >
            <vue-feather type="save" size="16" class="me-1"></vue-feather>
            Update Allowance
          </button>
        </div>
      </Modal>
      <!-- Add Allowance Modal End -->

      <!-- Add Recurrent Income Modal -->
      <Modal :open="incomeModal" @closed="incomeModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="trending-up" size="18" class="text-primary me-2"></vue-feather>
            {{ incomeMode === 'add' ? 'Add Recurrent Income' : 'Edit Recurrent Income' }}
          </h5>
          <button @click="incomeModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="row g-3">
              <div class="col-12">
                <label for="incomeName" class="form-label">Income Name</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="tag" size="16"></vue-feather>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    list="incomeOptions"
                    id="incomeName"
                    v-model="income.name"
                    placeholder="Enter income name or select from suggestions"
                  >
                </div>
                <datalist id="incomeOptions">
                  <option v-for="income in suggestions.incomes" :key="income" :value="income"></option>
                </datalist>
              </div>

              <div class="col-12">
                <label for="incomeAmount" class="form-label">Amount</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="dollar-sign" size="16"></vue-feather>
                  </span>
                  <input
                    type="number"
                    class="form-control"
                    id="incomeAmount"
                    v-model="income.amount"
                    placeholder="Enter amount"
                  >
                </div>
              </div>

              <div class="col-12">
                <label class="form-label d-block">Taxable</label>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="incomeTaxableOptions"
                    id="incomeTaxableYes"
                    :value="1"
                    v-model="income.taxable"
                  >
                  <label class="form-check-label" for="incomeTaxableYes">
                    <badge type="success" class="badge-sm">Yes</badge>
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="incomeTaxableOptions"
                    id="incomeTaxableNo"
                    :value="0"
                    v-model="income.taxable"
                  >
                  <label class="form-check-label" for="incomeTaxableNo">
                    <badge type="secondary" class="badge-sm">No</badge>
                  </label>
                </div>
                <small class="text-muted d-block mt-1">
                  Taxable incomes will be included in the employee's taxable income.
                </small>
              </div>

              <div class="col-md-6">
                <label for="incomeFrom" class="form-label">From Date</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <input
                    type="date"
                    class="form-control"
                    id="incomeFrom"
                    v-model="income.from"
                  >
                </div>
              </div>

              <div class="col-md-6">
                <label for="incomeTo" class="form-label">To Date</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <input
                    type="date"
                    class="form-control"
                    id="incomeTo"
                    v-model="income.to"
                  >
                </div>
              </div>

              <div class="col-12">
                <label for="incomeDescription" class="form-label">Description <span class="text-muted small">(optional)</span></label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="file-text" size="16"></vue-feather>
                  </span>
                  <textarea
                    id="incomeDescription"
                    class="form-control"
                    v-model="income.description"
                    rows="2"
                    placeholder="Enter a description for this income (optional)"
                  ></textarea>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="incomeModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="incomeMode === 'add'"
            @click="addIncome"
          >
            <vue-feather type="plus" size="16" class="me-1"></vue-feather>
            Add Income
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateIncome($event, income.id)"
          >
            <vue-feather type="save" size="16" class="me-1"></vue-feather>
            Update Income
          </button>
        </div>
      </Modal>
      <!-- Add Recurrent Income Modal End -->

      <!-- Add Recurrent Deduction Modal -->
      <Modal :open="deductionModal" @closed="deductionModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="trending-down" size="18" class="text-primary me-2"></vue-feather>
            {{ deductionMode === 'add' ? 'Add Recurrent Deduction' : 'Edit Recurrent Deduction' }}
          </h5>
          <button @click="deductionModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="row g-3">
              <div class="col-12">
                <label for="deductionName" class="form-label">Deduction Name</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="tag" size="16"></vue-feather>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    list="deductionOptions"
                    id="deductionName"
                    v-model="deduction.name"
                    placeholder="Enter deduction name or select from suggestions"
                  >
                </div>
                <datalist id="deductionOptions">
                  <option v-for="deduction in suggestions.deductions" :key="deduction" :value="deduction"></option>
                </datalist>
              </div>

              <div class="col-12">
                <label for="deductionAmount" class="form-label">Amount</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="dollar-sign" size="16"></vue-feather>
                  </span>
                  <input
                    type="number"
                    class="form-control"
                    id="deductionAmount"
                    v-model="deduction.amount"
                    placeholder="Enter amount"
                  >
                </div>
              </div>

              <div class="col-12">
                <label class="form-label d-block">Taxable</label>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="deductionTaxableOptions"
                    id="deductionTaxableYes"
                    :value="1"
                    v-model="deduction.taxable"
                  >
                  <label class="form-check-label" for="deductionTaxableYes">
                    <badge type="success" class="badge-sm">Yes</badge>
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="deductionTaxableOptions"
                    id="deductionTaxableNo"
                    :value="0"
                    v-model="deduction.taxable"
                  >
                  <label class="form-check-label" for="deductionTaxableNo">
                    <badge type="secondary" class="badge-sm">No</badge>
                  </label>
                </div>
                <small class="text-muted d-block mt-1">
                  Taxable deductions will affect the employee's taxable income calculation.
                </small>
              </div>

              <div class="col-md-6">
                <label for="deductionFrom" class="form-label">From Date</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <input
                    type="date"
                    class="form-control"
                    id="deductionFrom"
                    v-model="deduction.from"
                  >
                </div>
              </div>

              <div class="col-md-6">
                <label for="deductionTo" class="form-label">To Date</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <input
                    type="date"
                    class="form-control"
                    id="deductionTo"
                    v-model="deduction.to"
                  >
                </div>
              </div>

              <div class="col-12">
                <label for="deductionDescription" class="form-label">Description <span class="text-muted small">(optional)</span></label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="file-text" size="16"></vue-feather>
                  </span>
                  <textarea
                    id="deductionDescription"
                    class="form-control"
                    v-model="deduction.description"
                    rows="2"
                    placeholder="Enter a description for this deduction (optional)"
                  ></textarea>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="deductionModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="deductionMode === 'add'"
            @click="addDeduction"
          >
            <vue-feather type="plus" size="16" class="me-1"></vue-feather>
            Add Deduction
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateDeduction($event, deduction.id)"
          >
            <vue-feather type="save" size="16" class="me-1"></vue-feather>
            Update Deduction
          </button>
        </div>
      </Modal>
      <!-- Add Recurrent Deduction Modal End -->

      <!-- Add Employee Revenue Modal -->
      <Modal :open="employeeRevenueModal" @closed="employeeRevenueModal = false">
        <div class="modal-header border-0">
          <h5 class="modal-title m-0 d-flex align-items-center">
            <vue-feather type="bar-chart-2" size="18" class="text-primary me-2"></vue-feather>
            {{ employeeRevenueMode === 'add' ? 'Add Employee Revenue' : 'Edit Employee Revenue' }}
          </h5>
          <button @click="employeeRevenueModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="revenueMonth" class="form-label">Month</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <select
                    id="revenueMonth"
                    class="form-select"
                    v-model="employeeRevenue.month"
                   data="">
                    <option value="" disabled selected>Select month</option>
                    <option v-for="month in months" :key="month" :value="month">{{month}}</option>
                  </select>
                </div>
              </div>

              <div class="col-md-6">
                <label for="revenueYear" class="form-label">Year</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="calendar" size="16"></vue-feather>
                  </span>
                  <input
                    id="revenueYear"
                    type="number"
                    class="form-control"
                    v-model="employeeRevenue.year"
                    placeholder="Enter year"
                    min="2000"
                    max="2100"
                  />
                </div>
              </div>

              <div class="col-12">
                <label for="revenueAmount" class="form-label">Amount</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="dollar-sign" size="16"></vue-feather>
                  </span>
                  <input
                    id="revenueAmount"
                    type="number"
                    class="form-control"
                    v-model="employeeRevenue.amount"
                    placeholder="Enter revenue amount"
                  >
                </div>
              </div>

              <div class="col-12">
                <label for="commissionRate" class="form-label">Commission Rate</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="percent" size="16"></vue-feather>
                  </span>
                  <select
                    id="commissionRate"
                    class="form-select"
                    v-model="employeeRevenue.commission_rate_id"
                   data="">
                    <option value="" disabled selected>Select commission rate</option>
                    <option v-for="rate in allCommissionRates" :key="rate.id" :value="rate.id">
                      {{ rate.name }} ({{ rate.rate }}%) - Threshold {{ Ksh(rate.amount) }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-12">
                <label for="revenueDescription" class="form-label">Description <span class="text-muted small">(optional)</span></label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="file-text" size="16"></vue-feather>
                  </span>
                  <textarea
                    id="revenueDescription"
                    class="form-control"
                    v-model="employeeRevenue.description"
                    rows="2"
                    placeholder="Enter a description for this revenue (optional)"
                  ></textarea>
                </div>
              </div>

              <div v-if="commissionRate !== null && employeeRevenue.amount" class="col-12">
                <div class="commission-calculation p-3 bg-light rounded-3">
                  <div class="d-flex align-items-start">
                    <vue-feather type="info" size="20" class="text-primary me-2 mt-1"></vue-feather>
                    <div>
                      <h6 class="mb-1">Commission Calculation</h6>
                      <p class="mb-0 small">
                        Based on the <strong>{{ commissionRate.name }}</strong> category with a rate of
                        <badge type="info" class="badge-sm">{{ commissionRate.rate }}%</badge>,
                        the commission for this revenue would be approximately
                        <strong>{{ calculateCommission(employeeRevenue.amount, commissionRate) }}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="employeeRevenueModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="employeeRevenueMode === 'add'"
            :disabled="!employeeRevenue.month || !employeeRevenue.year || !employeeRevenue.amount"
            @click="addEmployeeRevenue"
          >
            <vue-feather type="plus" size="16" class="me-1"></vue-feather>
            Add Revenue
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-else
            :disabled="!employeeRevenue.month || !employeeRevenue.year || !employeeRevenue.amount"
            @click="updateEmployeeRevenue($event, employeeRevenue.id)"
          >
            <vue-feather type="save" size="16" class="me-1"></vue-feather>
            Update Revenue
          </button>
        </div>
      </Modal>
      <!-- Add Employee Revenue Modal End -->

    </Card>

  </div>
</template>

<script>
import employeeService from "@/api/employeeService";
import formatDate from "../../utils/formatDate";
import allowanceService from "@/api/allowanceService";
import incomeService from "@/api/incomeService";
import deductionService from "@/api/deductionService";
import fileRequestService from "@/api/fileRequestService";
import confirmDialog from "@/utils/confirmDialog";
import Ksh from "@/utils/formatNumber";
import commissionRateService from "@/api/commissionRateService";
import employeeRevenueService from "@/api/employeeRevenueService";
import analyticsService from "@/api/analyticsService";
import payrollService from "@/api/payrollService";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import notify from "@/utils/notify";
import loadingButton from "@/utils/loadingButton";

export default {
  name: "EmployeeDetails",
  data() {
    return {
      Ksh,
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ],
      profile: {},
      changeStatusModal: false,
      employeeStatus: '',
      employee: {
        details: {},
        kin: {},
        department: {},
        designation: {},
        station: {}
      },
      avatarUrl: process.env.VUE_APP_API_IMAGE_URL + 'employee/',
      loading: true,
      statsLoading: true,
      allowances: [],
      incomes: [],
      deductions: [],
      allowanceMode: 'add',
      allowanceModal:false,
      allowance: {
        employee_id: this.$route.params.id,
        name: '',
        amount: '',
        taxable: 1,
      },
      incomeMode: 'add',
      incomeModal:false,
      income: {
        employee_id: this.$route.params.id,
        name: '',
        amount: '',
        taxable: 1,
        from: '',
        to: '',
        description: '',
      },
      deductionMode: 'add',
      deductionModal:false,
      deduction: {
        employee_id: this.$route.params.id,
        name: '',
        amount: '',
        taxable: 1,
        from: '',
        to: '',
        description: '',
      },
      allCommissionRates: [],
      employeeRevenues: [],
      employeeRevenueMode: 'add',
      employeeRevenueModal:false,
      employeeRevenue: {
        employee_id: this.$route.params.id,
        month: '',
        year: '',
        commission_rate_id: '',
        amount: '',
      },
      p9Modal: false,
      year: new Date().getFullYear(),
      years: [],
      suggestions: {
        allowances: [],
        incomes: [],
        deductions: [],
        files: [],
      },
      file: {
        employee_id: this.$route.params.id,
        name: '',
        file: '',
        description: '',
        file_request_id: null
      },
      selectedFile: null,
      employeeFiles: [],
      employeeFileRequests: [],
      fileModal: false,
      fileRequestModal: false,
      savingFileRequest: false,
      fileRequestData: {
        name: '',
        description: ''
      },
      isEmployee: false,
      activeTab: 'details',
    }
  },
  methods: {
    confirmDialog,
    formatDate,
    formatNumber,
    getIconByStatus(status) {
      switch (status) {
        case 'active':
          return 'check-circle';
        case 'inactive':
          return 'x-circle';
        case 'suspended':
          return 'alert-circle';
        case 'terminated':
          return 'slash';
        default:
          return 'help-circle';
      }
    },
    getFileRequestStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'bg-warning text-dark';
        case 'submitted':
          return 'bg-success';
        case 'rejected':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },
    getFileIcon(fileName) {
      const extension = fileName.split('.').pop().toLowerCase();

      switch (extension) {
        case 'pdf':
          return 'file-text';
        case 'doc':
        case 'docx':
          return 'file-text';
        case 'xls':
        case 'xlsx':
          return 'file';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        default:
          return 'file';
      }
    },
    getStatusDescriptionClass(status) {
      switch (status) {
        case 'active':
          return 'bg-success-subtle';
        case 'inactive':
          return 'bg-danger-subtle';
        case 'suspended':
          return 'bg-warning-subtle';
        case 'terminated':
          return 'bg-dark-subtle';
        default:
          return 'bg-light';
      }
    },
    getStatusTitle(status) {
      switch (status) {
        case 'active':
          return 'Active Employee';
        case 'inactive':
          return 'Inactive Employee';
        case 'suspended':
          return 'Suspended Employee';
        case 'terminated':
          return 'Terminated Employee';
        default:
          return 'Unknown Status';
      }
    },
    getStatusDescription(status) {
      switch (status) {
        case 'active':
          return 'Employee is currently working and will be included in payroll calculations.';
        case 'inactive':
          return 'Employee is temporarily not working and will not be included in payroll calculations.';
        case 'suspended':
          return 'Employee has been suspended and will not be included in payroll calculations until reinstated.';
        case 'terminated':
          return 'Employee has been terminated and will no longer be included in payroll calculations.';
        default:
          return 'Status description not available.';
      }
    },
    calculateCommission(amount, commissionRate) {
      if (!amount || !commissionRate) return Ksh(0);

      // Only calculate commission if the amount is greater than the threshold
      if (amount > commissionRate.amount) {
        const commissionableAmount = amount - commissionRate.amount;
        const commission = commissionableAmount * (commissionRate.rate / 100);
        return Ksh(commission);
      }

      return Ksh(0);
    },
    async getEmployee() {
      this.employee = await employeeService.getEmployee(this.$route.params.id)
      this.loading = false
    },
    editEmployee() {
      this.$router.push('/edit-employee/' + this.$route.params.id);
    },
    async getAllowances() {
      this.allowances = await allowanceService.getAllowances(this.$route.params.id)
    },
    async getYears () {
      this.years = await analyticsService.getYears()
    },
    async getIncomes() {
      this.incomes = await incomeService.getIncomes(this.$route.params.id)
    },
    async getDeductions() {
      this.deductions = await deductionService.getDeductions(this.$route.params.id)
    },
    async getAllCommissionRates() {
      this.allCommissionRates = await commissionRateService.getCommissionRates()
    },
    async getEmployeeRevenues() {
      this.employeeRevenues = await employeeRevenueService.getEmployeeRevenues(this.$route.params.id)
    },
    async getEmployeeFiles() {
      this.employeeFiles = await employeeService.getEmployeeFiles(this.$route.params.id)
    },
    async getEmployeeFileRequests() {
      try {
        this.employeeFileRequests = await fileRequestService.getEmployeeFileRequests(this.$route.params.id)
      } catch (error) {
        console.error('Error fetching employee file requests:', error)
      }
    },
    viewFile(file) {
      const fileLocation = this.avatarUrl + file
      window.open(fileLocation, '_blank');
    },
    async getAllowanceSuggestionNames() {
      if (sessionStorage.getItem('allowanceNames') === null) {
        let suggestions = await employeeService.allowanceNames()
        this.suggestions = suggestions
        sessionStorage.setItem('allowanceNames', JSON.stringify(suggestions))
      } else {
        this.suggestions = JSON.parse(sessionStorage.getItem('allowanceNames'))
      }
    },
    async changeStatus(e) {
      loadingButton(e)
      await employeeService.updateEmployeeStatus(
          { status: this.employeeStatus }, this.$route.params.id
      )
      loadingButton(e, false)
    },
    async p9Download(e) {
      loadingButton(e)
      let response = await payrollService.p9Download(this.year, this.$route.params.id, this.name)
      if (response)
        this.p9Modal = false
      loadingButton(e, false)
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
        case 'suspended':
          return 'warning';
        case 'terminated':
          return 'dark';
      }
    },
    changeAvatar : async function(file) {
      let fileReader = this.processFile(file)
      fileReader.onload = async () => {
        if (typeof fileReader.result === 'string')
          await employeeService.updateAvatar({
            file: fileReader.result,
            employee_id: this.$route.params.id
          })
      }
    },
    handleFileUpload(file) {
      this.selectedFile = file
    },
    openFileModal() {
      // Reset file data before opening modal
      this.file = {
        employee_id: this.$route.params.id,
        name: '',
        file: '',
        description: '',
        file_request_id: null
      };
      this.selectedFile = null;
      this.fileModal = true;
    },
    openFileModalForRequest(request) {
      // Pre-populate the file modal with request data
      this.file.name = request.file_request.name;
      this.file.description = request.file_request.description || '';
      this.file.file_request_id = request.id;
      this.selectedFile = null;
      this.fileModal = true;
    },
    closeFileModal() {
      this.fileModal = false;
      // Reset file data when modal is closed
      this.file = {
        employee_id: this.$route.params.id,
        name: '',
        file: '',
        description: '',
        file_request_id: null
      };
      this.selectedFile = null;
    },
    addFile : async function(e) {
      if (this.selectedFile === null) {
        notify.alert.error('Please select a file')
      } else {
        loadingButton(e)
        let fileReader = this.processFile(this.selectedFile)
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            this.file.file = fileReader.result
            const hasFileRequest = this.file.file_request_id !== null
            let response = await employeeService.addEmployeeFile(this.file)
            this.updateSuggestions('files', this.file.name)
            if (response)
              this.closeFileModal()
              await this.getEmployeeFiles()
              // Refresh file requests if this was uploaded for a request
              if (hasFileRequest) {
                await this.getEmployeeFileRequests()
              }
        }
        loadingButton(e, false)
      }
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
    async submitEmployeeFileRequest() {
      if (!this.fileRequestData.name) {
        return;
      }

      this.savingFileRequest = true;
      try {
        const requestPayload = {
          name: this.fileRequestData.name,
          description: this.fileRequestData.description,
          employee_ids: [this.$route.params.id]
        };
        const response = await fileRequestService.createFileRequest(requestPayload);
        if (response) {
          this.fileRequestModal = false;
          this.fileRequestData = {
            name: '',
            description: ''
          };
          // Refresh file requests after successful creation
          await this.getEmployeeFileRequests();
        }
      } catch (error) {
        console.error('Error requesting file:', error);
      } finally {
        this.savingFileRequest = false;
      }
    },
    populateAllowanceEdit(allowance) {
      this.allowance = allowance
    },
    async addAllowance(e) {
      loadingButton(e)
      let response = await allowanceService.createAllowance(this.allowance)
      this.updateSuggestions('allowances', this.allowance.name)
      if (response)
        this.allowanceModal = false
        await this.getAllowances()
      loadingButton(e, false)
    },
    async updateAllowance(e, id) {
      loadingButton(e)
      let response = await allowanceService.updateAllowance(this.allowance, id)
      if (response)
        this.allowanceModal = false
        await this.getAllowances()
      loadingButton(e, false)
    },
    async deleteAllowance(id) {
      await allowanceService.deleteAllowance(id)
      await this.getAllowances()
    },
    populateIncomeEdit(income) {
      this.income = income
    },
    async addIncome(e) {
      loadingButton(e)
      let response = await incomeService.createIncome(this.income)
      this.updateSuggestions('incomes', this.income.name)
      if (response)
        this.incomeModal = false
        await this.getIncomes()
      loadingButton(e, false)
    },
    async updateIncome(e, id) {
      loadingButton(e)
      let response = await incomeService.updateIncome(this.income, id)
      if (response)
        this.incomeModal = false
        await this.getIncomes()
      loadingButton(e, false)
    },
    async deleteIncome(id) {
      await incomeService.deleteIncome(id)
      await this.getIncomes()
    },
    populateDeductionEdit(deduction) {
      this.deduction = deduction
    },
    async addDeduction(e) {
      loadingButton(e)
      let response = await deductionService.createDeduction(this.deduction)
      this.updateSuggestions('deductions', this.deduction.name)
      if (response)
        this.deductionModal = false
        await this.getDeductions()
      loadingButton(e, false)
    },
    async updateDeduction(e, id) {
      loadingButton(e)
      let response = await deductionService.updateDeduction(this.deduction, id)
      if (response)
        this.deductionModal = false
        await this.getDeductions()
      loadingButton(e, false)
    },
    async deleteDeduction(id) {
      await deductionService.deleteDeduction(id)
      await this.getDeductions()
    },
    populateEmployeeRevenueEdit(employeeRevenue) {
      this.employeeRevenue = employeeRevenue
    },
    async addEmployeeRevenue(e) {
      loadingButton(e)
      let response = await employeeRevenueService.createEmployeeRevenue(this.employeeRevenue)
      if (response)
        this.employeeRevenueModal = false
        await this.getEmployeeRevenues()
      loadingButton(e, false)
    },
    async updateEmployeeRevenue(e, id) {
      loadingButton(e)
      let response = await employeeRevenueService.updateEmployeeRevenue(this.employeeRevenue, id)
      if (response)
        this.employeeRevenueModal = false
        await this.getEmployeeRevenues()
      loadingButton(e, false)
    },
    updateSuggestions(suggestionList, suggestion) {
      let suggestions = JSON.parse(sessionStorage.getItem('allowanceNames'))
      if (!suggestions[suggestionList].includes(suggestion)) {
        suggestions[suggestionList].push(suggestion)
        sessionStorage.setItem('allowanceNames', JSON.stringify(suggestions))
        this.suggestions = suggestions
      }
    },
  },

  watch: {
    activeTab(newTab) {
      // Save the active tab to localStorage for this employee
      localStorage.setItem(`employee-details-tab-${this.$route.params.id}`, newTab);
    },
    allowanceModal: function () {
      if (!this.allowanceModal) {
        this.allowance = {
          employee_id: this.$route.params.id,
        }
      }
    },
    incomeModal: function () {
      if (!this.incomeModal) {
        this.income = {
          employee_id: this.$route.params.id,
        }
      }
    },
    deductionModal: function () {
      if (!this.deductionModal) {
        this.deduction = {
          employee_id: this.$route.params.id,
        }
      }
    },
    employeeRevenuesModal: function () {
      if (!this.employeeRevenuesModal) {
        this.employeeRevenue = {
          employee_id: this.$route.params.id,
        }
      }
    },
    fileModal: function () {
      if (!this.fileModal) {
        this.file = {
          employee_id: this.$route.params.id,
        }
      }
    }
  },
  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.isEmployee = this.profile.role === 'employee'
    await this.getEmployee()
    this.employeeStatus = this.employee.status
    this.employeeRevenue.month = new Date().toLocaleString('default', {month: 'long'});
    this.employeeRevenue.year = new Date().getFullYear();
    await this.getYears()
    await this.getAllowances()
    await this.getIncomes()
    await this.getDeductions()
    await this.getAllCommissionRates()
    await this.getEmployeeRevenues()
    await this.getEmployeeFiles()
    await this.getEmployeeFileRequests()
    await this.getAllowanceSuggestionNames()
    // Restore previously selected tab for this employee, or default to 'details'
    const savedTab = localStorage.getItem(`employee-details-tab-${this.$route.params.id}`);
    if (savedTab) {
      this.activeTab = savedTab;
    }
    // If no saved tab, activeTab remains 'details' from data() default
  },

  computed: {
    commissionRate() {
      const id = this.employeeRevenue && this.employeeRevenue.commission_rate_id
      if (!id) return null
      return this.allCommissionRates.find(r => r.id === id) || null
    },
    totalRevenue() {
      return this.employeeRevenues.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
    },
    totalCommission() {
      return this.employeeRevenues.reduce((sum, r) => {
        const amount = Number(r.amount) || 0
        const target = Number(r.target) || 0
        const rate = Number(r.rate) || 0
        return amount < target ? sum : sum + ((amount - target) * (rate / 100))
      }, 0)
    },
    name() {
      return this.employee.first_name + " " + this.employee.middle_name + " " + this.employee.last_name
    },
    avatar() {
      return this.employee.avatar !== null ? this.avatarUrl + this.employee.avatar : 'https://villagesonmacarthur.com/wp-content/uploads/2020/12/Blank-Avatar.png'
    }
  }
}
</script>

<style scoped>
/* Employee profile styles */
.employee-profile {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Detail item styles */
.detail-item {
  margin-bottom: 0.75rem;
}

/* Badge styles */
.badge-lg {
  font-size: 0.875rem;
  padding: 0.35rem 0.65rem;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Tabs container styling for better background contrast */
.nav-tabs {
  border-bottom: 0;
  background: #f3f6fb;
  padding: 0.25rem;
  border-radius: 0.5rem;
}

.nav-tabs .nav-link {
  color: #0255a5;
  border: none;
  border-bottom: 0;
  padding: 1rem 1.25rem;
  font-weight: 600;
  border-radius: 0.375rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-tabs .nav-link:hover {
  color: #113c61;
}

.nav-tabs .nav-link.active {
  color: #0255a5;
  background-color: rgba(2, 85, 165, 0.10);
  box-shadow: inset 0 -3px 0 #0255a5;
}

/* Improve hover visibility for non-active tabs */
.nav-tabs .nav-link:not(.active):hover {
  background-color: rgba(2, 85, 165, 0.06);
}

/* Table styles */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

/* Form styles */
.input-group-text {
  color: #6c757d;
}

/* Status description styles */
.status-description {
  border-left: 4px solid;
}

.bg-success-subtle {
  background-color: rgba(25, 135, 84, 0.1);
  border-left-color: #198754;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
  border-left-color: #dc3545;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
  border-left-color: #ffc107;
}

.bg-dark-subtle {
  background-color: rgba(33, 37, 41, 0.1);
  border-left-color: #212529;
}

/* Commission calculation styles */
.commission-calculation, .commission-details {
  border-left: 4px solid #0d6efd;
}
</style>
